import { useState, useEffect, useRef, useCallback } from 'react';
import type { InteractionMessageResponse } from '@vagusx/clients';
import { API_URL } from '@/lib/env';
import { useInteractionMessages } from './use-interactions';

/**
 * Connection state for the SSE stream.
 */
export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

/**
 * Options for the live transcript hook.
 */
export interface UseLiveTranscriptOptions {
  /** Enable live streaming (default: true when interaction is in_progress) */
  enabled?: boolean;
  /** Callback when a new message arrives */
  onMessage?: (message: InteractionMessageResponse) => void;
  /** Callback when connection state changes */
  onConnectionStateChange?: (state: ConnectionState) => void;
}

/**
 * Hook result for live transcript.
 */
export interface UseLiveTranscriptResult {
  /** All messages (initial + live) */
  messages: InteractionMessageResponse[];
  /** Whether initial messages are loading */
  isLoading: boolean;
  /** Whether connected to SSE stream */
  isConnected: boolean;
  /** Current connection state */
  connectionState: ConnectionState;
  /** Manually reconnect to the stream */
  reconnect: () => void;
  /** Manually disconnect from the stream */
  disconnect: () => void;
}

/**
 * Hook for real-time transcript streaming via SSE.
 *
 * Combines initial messages from the API with live updates from SSE.
 * Automatically handles reconnection and deduplication.
 *
 * @param interactionId - The interaction ID to stream
 * @param isLive - Whether the interaction is currently in progress
 * @param options - Additional options
 */
export function useLiveTranscript(
  interactionId: string | undefined,
  isLive: boolean,
  options: UseLiveTranscriptOptions = {}
): UseLiveTranscriptResult {
  const { enabled = true, onMessage, onConnectionStateChange } = options;

  const [liveMessages, setLiveMessages] = useState<InteractionMessageResponse[]>([]);
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const eventSourceRef = useRef<EventSource | null>(null);
  const seenMessageIds = useRef<Set<string>>(new Set());
  // Mirror of `connectionState` for use inside event-listener callbacks.
  // EventSource handlers are registered once and capture the value at
  // registration; without the ref the closure reads `'connecting'`
  // forever and we can't tell whether we need to flip back to
  // `'connected'` after a transient `error`.
  const connectionStateRef = useRef<ConnectionState>('disconnected');
  // Diagnostic for the persistent "Connection error — reconnecting…"
  // banner. `lastEventAtRef` lets the `error` log report how long
  // since the last successful event so we can tell idle-timeout from
  // instant drop. Pairs with `[sse:...]` logs on the API side.
  const lastEventAtRef = useRef<number>(Date.now());
  // Staleness watchdog: when the TCP path silently goes half-open
  // (server keeps writing, browser receives nothing), EventSource
  // doesn't always fire `error` quickly. The api8/browser correlation
  // showed the server wrote 8 unanswered heartbeats over ~95s before
  // the browser surfaced the failure. Watchdog: if no event arrives
  // for STALENESS_MS, force-close the current EventSource and create
  // a fresh one. Resets on every event.
  const stalenessTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const STALENESS_MS = 15000;
  // The server emits `event: 'end'` once and then closes the stream
  // when the interaction reaches a terminal status (completed / failed
  // / transferred). EventSource has no notion of a graceful server
  // close — the close fires as a generic `error` event. Without this
  // flag, every clean end-of-call surfaces as "Connection error —
  // reconnecting…" plus a doomed reconnect cycle until the LiveKit
  // room is torn down and `isLive` flips to false. Once set, we
  // suppress the error banner, skip the post-error safety reconnect,
  // and stop the staleness watchdog from doing anything useful.
  const terminalReceivedRef = useRef<boolean>(false);

  // Load existing messages via React Query
  const { data: initialMessages, isLoading } = useInteractionMessages(interactionId);

  // Track seen message IDs from initial load
  useEffect(() => {
    if (initialMessages) {
      seenMessageIds.current = new Set(initialMessages.map((m) => m.id));
    }
  }, [initialMessages]);

  // Update connection state and notify. Skip the no-op transition so
  // the on-message recovery path below doesn't fire the
  // onConnectionStateChange callback on every single message.
  const updateConnectionState = useCallback(
    (state: ConnectionState) => {
      if (connectionStateRef.current === state) return;
      connectionStateRef.current = state;
      setConnectionState(state);
      onConnectionStateChange?.(state);
    },
    [onConnectionStateChange]
  );

  // Reset the staleness watchdog. Called on every successful event
  // (connected / message / heartbeat) so a healthy stream keeps the
  // timer pushed forward.
  const resetStalenessWatchdog = useCallback(() => {
    if (stalenessTimerRef.current) {
      clearTimeout(stalenessTimerRef.current);
    }
    // No point watching a stream the server has already ended.
    if (terminalReceivedRef.current) return;
    stalenessTimerRef.current = setTimeout(() => {
      if (terminalReceivedRef.current) return;
      console.warn(`[sse-client] WATCHDOG FIRING — stale ${STALENESS_MS}ms`);
      // Force-close the current EventSource and create a fresh one.
      // Half-open TCP is the failure we're guarding against: server
      // keeps writing, network drops it, EventSource doesn't fire
      // `error` quickly. A fresh connection establishes a new TCP
      // path; the server-side stream loop is still running and will
      // serve our new request.
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      stalenessTimerRef.current = null;
      // Fire reconnect synchronously — the closure has all the
      // setup state it needs.
      void Promise.resolve().then(() => connect());
    }, STALENESS_MS);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- connect captured in body, ref-stable

  // Connect to SSE stream
  const connect = useCallback(() => {
    if (!interactionId || !isLive || !enabled) return;
    if (eventSourceRef.current) return; // Already connected
    // Don't even try if the server has already signaled end. A fresh
    // connect would just re-receive the `end` event and loop.
    if (terminalReceivedRef.current) return;

    updateConnectionState('connecting');

    const eventSource = new EventSource(`${API_URL}/api/interactions/${interactionId}/stream`, {
      withCredentials: true,
    });
    eventSourceRef.current = eventSource;
    resetStalenessWatchdog();

    eventSource.addEventListener('connected', () => {
      lastEventAtRef.current = Date.now();
      resetStalenessWatchdog();
      updateConnectionState('connected');
    });

    eventSource.addEventListener('message', (event) => {
      // A live message proves the stream is healthy — clear any prior
      // transient 'error' state. EventSource auto-reconnects on
      // network blips but never re-emits the server's 'connected'
      // event, so without this recovery the banner can stick across
      // the rest of the call. updateConnectionState is a no-op if the
      // state is already 'connected'.
      lastEventAtRef.current = Date.now();
      resetStalenessWatchdog();
      updateConnectionState('connected');
      try {
        const message = JSON.parse(event.data) as InteractionMessageResponse;

        // Deduplicate messages
        if (seenMessageIds.current.has(message.id)) {
          return;
        }
        seenMessageIds.current.add(message.id);

        // Add to live messages. We deliberately do NOT mirror into the React
        // Query cache — the hook below merges `initialMessages + liveMessages`,
        // so writing to the cache would make the same message appear twice
        // (once from cache, once from live state). Persistence is handled by
        // the backend; a remount refetches the authoritative list.
        setLiveMessages((prev) => [...prev, message]);
        onMessage?.(message);
      } catch (err) {
        console.error('[SSE] Failed to parse message:', err);
      }
    });

    eventSource.addEventListener('end', () => {
      // Server has emitted the terminal `end` event and is about to
      // close the stream. EventSource will surface that close as a
      // generic `error` — without this listener, we'd flip to 'error'
      // and start the reconnect dance. Mark terminal, cancel
      // watchdog, close cleanly, settle to 'disconnected'.
      terminalReceivedRef.current = true;
      if (stalenessTimerRef.current) {
        clearTimeout(stalenessTimerRef.current);
        stalenessTimerRef.current = null;
      }
      eventSource.close();
      eventSourceRef.current = null;
      updateConnectionState('disconnected');
    });

    eventSource.addEventListener('heartbeat', () => {
      // Server proved it's alive — clear any stale 'error' state for
      // the same reason as the message handler above.
      lastEventAtRef.current = Date.now();
      resetStalenessWatchdog();
      updateConnectionState('connected');
    });

    eventSource.addEventListener('error', () => {
      // EventSource auto-reconnects; we just flip the state so the UI can
      // indicate the stream is degraded. The next 'message' / 'heartbeat'
      // event clears it again.
      //
      // Diagnostic: readyState=0 means EventSource is actively
      // reconnecting (legit). readyState=1 means OPEN — error event
      // fired spuriously and the connection is actually fine.
      // readyState=2 means CLOSED — terminal; no auto-reconnect.
      // `gapSinceLastEvent` distinguishes idle-timeout (long gap)
      // from instant drop (short gap).
      const gap = Date.now() - lastEventAtRef.current;
      console.log(
        `[sse-client] ERROR readyState=${eventSource.readyState} gapSinceLastEvent=${gap}ms terminal=${terminalReceivedRef.current}`
      );
      // Graceful end already signaled by the server — the `error`
      // we're seeing is the post-close artifact. Don't flip the
      // banner, don't reconnect.
      if (terminalReceivedRef.current) {
        eventSource.close();
        if (eventSourceRef.current === eventSource) eventSourceRef.current = null;
        return;
      }
      updateConnectionState('error');

      // Belt-and-suspenders safety net independent of the staleness
      // watchdog. After an `error` event we wait 2s — long enough for
      // EventSource's own auto-reconnect to land a `connected` or
      // `message`/`heartbeat` (which would set readyState=1 and we
      // skip below). If the stream is still not OPEN after 2s, the
      // path is genuinely dead — force-close and create a fresh
      // EventSource. Catches the half-open case where the watchdog
      // closure is stale (observed in api9 / browser correlation).
      setTimeout(() => {
        // Bail if a newer EventSource has replaced this one already.
        if (eventSourceRef.current !== eventSource) return;
        // Healthy after the blip — let it ride.
        if (eventSource.readyState === 1) return;
        // Server signaled end while we were waiting — no reconnect.
        if (terminalReceivedRef.current) return;
        console.warn(
          `[sse-client] POST-ERROR SAFETY RECONNECT — readyState=${eventSource.readyState}`
        );
        eventSource.close();
        eventSourceRef.current = null;
        connect();
      }, 2000);
    });
  }, [interactionId, isLive, enabled, onMessage, updateConnectionState]);

  // Disconnect from SSE stream
  const disconnect = useCallback(() => {
    if (stalenessTimerRef.current) {
      clearTimeout(stalenessTimerRef.current);
      stalenessTimerRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      updateConnectionState('disconnected');
    }
  }, [updateConnectionState]);

  // Reconnect to SSE stream
  const reconnect = useCallback(() => {
    disconnect();
    setLiveMessages([]);
    // A manual reconnect is the user telling us "try again" — clear
    // the terminal latch so connect() doesn't short-circuit.
    terminalReceivedRef.current = false;
    connect();
  }, [disconnect, connect]);

  // Reset the terminal latch when the target interaction changes —
  // a new call shouldn't inherit a prior call's "already ended" state.
  useEffect(() => {
    terminalReceivedRef.current = false;
  }, [interactionId]);

  // Connect/disconnect based on live state
  useEffect(() => {
    if (interactionId && isLive && enabled) {
      connect();
    } else {
      disconnect();
      setLiveMessages([]);
    }

    return () => {
      disconnect();
    };
  }, [interactionId, isLive, enabled, connect, disconnect]);

  // Merge initial and live messages, deduplicating by id. Without this,
  // a React Query refetch of initialMessages while SSE has already
  // populated liveMessages causes React "Encountered two children with
  // the same key" warnings — the conversation view ends up with
  // duplicated bubbles that React silently drops during reconciliation,
  // making the transcript appear partially missing after a call ends.
  const byId = new Map<string, InteractionMessageResponse>();
  for (const m of initialMessages ?? []) byId.set(m.id, m);
  for (const m of liveMessages) byId.set(m.id, m);
  const messages = Array.from(byId.values()).sort((a, b) => {
    const timeA = new Date(a.timestamp).getTime();
    const timeB = new Date(b.timestamp).getTime();
    return timeA - timeB;
  });

  return {
    messages,
    isLoading,
    isConnected: connectionState === 'connected',
    connectionState,
    reconnect,
    disconnect,
  };
}
